import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  InspectionFinding,
  SiteInspection,
} from 'services/ParkingAgentService';
import ReferenceReport from './ReferenceReport';

interface InspectionReportProps {
  siteId: number;
  report: SiteInspection;
}

function sourceLink(
  siteId: number,
  finding: InspectionFinding,
  referenceSource?: 'network' | 'material',
): string {
  if (finding.map_id) return `/project/${siteId}/map-editor/${finding.map_id}`;
  if (finding.code.startsWith('baseline_nvr'))
    return `/project/${siteId}?section=nvr`;
  if (finding.code.startsWith('baseline_map'))
    return `/project/${siteId}/map-editor`;
  if (finding.code.startsWith('baseline_materials'))
    return `/project/${siteId}/materials`;
  if (finding.code.startsWith('baseline_'))
    return referenceSource === 'network'
      ? `/project/${siteId}?section=network`
      : `/project/${siteId}/materials`;
  if (finding.code.startsWith('nvr_')) return `/project/${siteId}?section=nvr`;
  if (finding.code.startsWith('roi_')) return `/project/${siteId}/roi-editor`;
  return `/project/${siteId}/map-editor`;
}

const InspectionReport: React.FC<InspectionReportProps> = ({
  siteId,
  report,
}) => {
  const [severity, setSeverity] = useState('all');
  const [page, setPage] = useState(1);
  const filtered = useMemo(
    () =>
      report.findings.filter(
        (f) => severity === 'all' || f.severity === severity,
      ),
    [report, severity],
  );
  const counts = useMemo(
    () =>
      report.findings.reduce(
        (acc, f) => ({ ...acc, [f.severity]: acc[f.severity] + 1 }),
        { error: 0, warning: 0, info: 0 },
      ),
    [report],
  );
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(filtered.length / 15)),
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {new Date(report.checked_at).toLocaleString('ko-KR')} 기준 · 저장된 설정
        점검
      </Typography>
      {report.coverage === 'partial' && (
        <Alert severity="info">
          일부 데이터가 없어 전체 점검을 완료하지 못했습니다. 아래 확인 불가
          항목을 확인해주세요.
        </Alert>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip label={`NVR 문서 ${report.documents}개`} />
        <Chip label={`채널 ${report.channels}개`} />
        <Chip label={`CCTV ${report.cameras}개`} />
        <Chip label={`주차면 ${report.parkings}개`} />
        <Chip label={`ROI 파일 ${report.roi_files}개`} />
      </Stack>
      {report.reference && <ReferenceReport report={report.reference} />}
      <TextField
        select
        label="점검 결과 필터"
        value={severity}
        onChange={(e) => {
          setSeverity(e.target.value);
          setPage(1);
        }}
        size="small"
        sx={{ maxWidth: 320 }}
      >
        <MenuItem value="all">전체 {report.findings.length}건</MenuItem>
        <MenuItem value="error">수정 필요 {counts.error}건</MenuItem>
        <MenuItem value="warning">확인 필요 {counts.warning}건</MenuItem>
        <MenuItem value="info">확인 불가 {counts.info}건</MenuItem>
      </TextField>
      {report.findings.length === 0 && (
        <Alert severity={report.coverage === 'complete' ? 'success' : 'info'}>
          {report.coverage === 'complete'
            ? '검사한 저장 데이터에서 불일치가 발견되지 않았습니다.'
            : '현재 확인 가능한 데이터에서는 불일치가 발견되지 않았습니다.'}
        </Alert>
      )}
      {filtered
        .slice((currentPage - 1) * 15, currentPage * 15)
        .map((finding, index) => (
          <Paper
            variant="outlined"
            key={`${finding.code}-${finding.source}-${index}`}
            sx={{ p: 2, minWidth: 0 }}
          >
            <Stack spacing={1}>
              <Box>
                <Chip
                  size="small"
                  color={finding.severity}
                  label={
                    {
                      error: '수정 필요',
                      warning: '확인 필요',
                      info: '확인 불가',
                    }[finding.severity]
                  }
                />
              </Box>
              <Typography sx={{ overflowWrap: 'anywhere' }}>
                {finding.message}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {finding.source}
              </Typography>
              <Box>
                <Button
                  href={sourceLink(siteId, finding, report.reference?.source)}
                  sx={{ minHeight: 44 }}
                >
                  관련 데이터 열기
                </Button>
              </Box>
            </Stack>
          </Paper>
        ))}
      {filtered.length > 15 && (
        <Pagination
          count={Math.ceil(filtered.length / 15)}
          page={currentPage}
          onChange={(_, p) => setPage(p)}
          siblingCount={0}
          size="small"
        />
      )}
      <Box component="details" sx={{ overflowWrap: 'anywhere' }}>
        <Box component="summary" sx={{ cursor: 'pointer', py: 1.5 }}>
          검사한 원본 {report.sources.length}개
        </Box>
        {report.sources.map((source, i) => (
          <Typography
            key={i}
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            {source.name} · {source.sha256.slice(0, 12)}
          </Typography>
        ))}
      </Box>
    </Stack>
  );
};

export default InspectionReport;
